import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import TileWMS from 'ol/source/TileWMS';
import TileArcGISRest from 'ol/source/TileArcGISRest';
import TileGrid from 'ol/tilegrid/TileGrid';
import { createXYZ } from 'ol/tilegrid';
import { get as getProjection } from 'ol/proj';
import type TileSource from 'ol/source/Tile';

import type { SourceDef } from './types';
import { effectiveDef, extentOf, needsEpoch } from './catalogue';
import { scaleToResolution } from './projections';

const VIEW_PROJECTION = 'EPSG:3857';

function yesterdayUTC(): string {
  const d = new Date(Date.now() - 24 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

/** Substitute {time} and {date} in a URL template. */
function resolveUrl(def: SourceDef, timeId?: string): string {
  let url = def.url;
  if (timeId) url = url.replaceAll('{time}', timeId);
  if (def.datePlaceholder === 'yesterday') url = url.replaceAll('{date}', yesterdayUTC());
  return url;
}

function needsCrossOrigin(def: SourceDef): boolean {
  return Boolean(def.crossOrigin || def.reproject || def.projection !== VIEW_PROJECTION);
}

function customTileGrid(def: SourceDef): TileGrid | undefined {
  if (!def.tileGrid) return undefined;
  const proj = getProjection(def.projection);
  const extent = proj?.getExtent() ?? undefined;
  const { origin, tileSize, scaleDenominators } = def.tileGrid;
  if (!scaleDenominators) {
    // Grid published without a scale list: derive it from the source extent.
    if (!extent) throw new Error(`source ${def.id}: tileGrid needs scaleDenominators or a projection extent`);
    const width = extent[2] - extent[0];
    const resolutions = Array.from({ length: 13 }, (_, i) => width / tileSize / 2 ** i);
    return new TileGrid({ origin, tileSize, resolutions, extent });
  }
  return new TileGrid({
    origin,
    tileSize,
    resolutions: scaleDenominators.map(scaleToResolution),
    extent,
  });
}

function buildSource(def: SourceDef, timeId?: string): TileSource {
  const url = resolveUrl(def, timeId);
  const crossOrigin = needsCrossOrigin(def) ? 'anonymous' : undefined;
  const projection = def.projection;

  switch (def.type) {
    case 'xyz':
      return new XYZ({
        url,
        projection,
        crossOrigin,
        tileGrid: customTileGrid(def),
        maxZoom: def.tileGrid ? undefined : def.maxZoom,
        tileSize: def.tileGrid ? undefined : (def.tileSize ?? 256),
        attributions: def.attribution,
        transition: 120,
      });

    case 'wms': {
      if (!def.wms) throw new Error(`source ${def.id}: type wms without a wms block`);
      const layers = timeId ? def.wms.layers.replaceAll('{time}', timeId) : def.wms.layers;
      return new TileWMS({
        url,
        projection,
        crossOrigin,
        params: {
          LAYERS: layers,
          FORMAT: def.wms.format,
          TRANSPARENT: def.wms.transparent,
          VERSION: def.wms.version,
        },
        tileGrid: createXYZ({ tileSize: def.tileSize ?? 256, maxZoom: def.maxZoom ?? 21 }),
        attributions: def.attribution,
        transition: 120,
      });
    }

    case 'arcgis-export': {
      if (!def.arcgis) throw new Error(`source ${def.id}: type arcgis-export without an arcgis block`);
      const layers = timeId ? def.arcgis.layers.replaceAll('{time}', timeId) : def.arcgis.layers;
      return new TileArcGISRest({
        // TileArcGISRest appends /export itself.
        url: url.replace(/\/export$/, ''),
        projection,
        crossOrigin,
        params: { layers, format: def.arcgis.format },
        tileGrid: createXYZ({ tileSize: def.tileSize ?? 256, maxZoom: def.maxZoom ?? 21 }),
        attributions: def.attribution,
        transition: 120,
      });
    }
  }
}

export function buildLayer(parent: SourceDef, timeId: string | undefined, opacity: number): TileLayer<TileSource> {
  const def = effectiveDef(parent, timeId);
  if (needsEpoch(def) && !timeId) {
    throw new Error(`source ${def.id}: needs an epoch before it can be addressed`);
  }
  const layer = new TileLayer({
    source: buildSource(def, timeId),
    opacity,
    extent: extentOf(def),
    preload: 1,
  });
  if (def.minZoom !== undefined) layer.setMinZoom(def.minZoom - 0.01);
  layer.set('sourceId', def.id);
  layer.set('timeId', timeId);
  return layer;
}

/**
 * A tiny GetMap/export request over one spot, used to find out whether an epoch
 * actually covers the current view. See coverage.ts.
 */
export function probeUrl(parent: SourceDef, timeId: string | undefined, bbox: [number, number, number, number], size = 8): string | undefined {
  const def = effectiveDef(parent, timeId);
  const url = resolveUrl(def, timeId);
  const b = bbox.join(',');
  if (def.type === 'wms' && def.wms) {
    const layers = timeId ? def.wms.layers.replaceAll('{time}', timeId) : def.wms.layers;
    const q = new URLSearchParams({
      SERVICE: 'WMS',
      VERSION: def.wms.version,
      REQUEST: 'GetMap',
      LAYERS: layers,
      STYLES: '',
      CRS: VIEW_PROJECTION,
      BBOX: b,
      WIDTH: String(size),
      HEIGHT: String(size),
      FORMAT: 'image/png',
      TRANSPARENT: 'TRUE',
    });
    return `${url}${url.includes('?') ? '&' : '?'}${q}`;
  }
  if (def.type === 'arcgis-export' && def.arcgis) {
    const layers = timeId ? def.arcgis.layers.replaceAll('{time}', timeId) : def.arcgis.layers;
    const q = new URLSearchParams({
      bbox: b,
      bboxSR: String(def.arcgis.bboxSR),
      imageSR: String(def.arcgis.imageSR),
      size: `${size},${size}`,
      layers,
      format: 'png32',
      transparent: 'true',
      f: 'image',
    });
    return `${url}?${q}`;
  }
  return undefined;
}
